import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Download, Trash2, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { exportMyData, deleteMyAccount } from "@/lib/gdpr.functions";

export const Route = createFileRoute("/_authenticated/account/privacy")({
  component: AccountPrivacy,
});

function AccountPrivacy() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const exportFn = useServerFn(exportMyData);
  const deleteFn = useServerFn(deleteMyAccount);

  const meta = useMemo(
    () => (user?.user_metadata ?? {}) as Record<string, unknown>,
    [user],
  );
  const [newsletter, setNewsletter] = useState(false);
  const [savingNews, setSavingNews] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    setNewsletter(Boolean(meta.newsletter_opt_in));
  }, [meta]);

  const toggleNewsletter = async (next: boolean) => {
    setNewsletter(next);
    setSavingNews(true);
    const { error } = await supabase.auth.updateUser({
      data: { newsletter_opt_in: next, newsletter_opt_in_at: next ? new Date().toISOString() : null },
    });
    setSavingNews(false);
    if (error) {
      setNewsletter(!next);
      toast.error("Δεν αποθηκεύτηκε η προτίμηση. Δοκίμασε ξανά.");
      return;
    }
    toast.success(next ? "Εγγράφηκες στο newsletter! 🐼" : "Διαγράφηκες από το newsletter.");
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportFn();
      const blob = new Blob([res.export], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mrpanda-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Τα δεδομένα σου κατέβηκαν! 🐼");
    } catch {
      toast.error("Κάτι πήγε στραβά κατά τη λήψη. Δοκίμασε ξανά.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteFn();
      await supabase.auth.signOut();
      toast.success("Ο λογαριασμός σου διαγράφηκε.");
      navigate({ to: "/" });
    } catch {
      toast.error("Κάτι πήγε στραβά κατά τη διαγραφή. Δοκίμασε ξανά.");
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-1 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-bold">Απόρρητο & δεδομένα</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Διαχειρίσου τις προτιμήσεις σου και άσκησε τα δικαιώματά σου σύμφωνα με το GDPR.
        </p>
      </div>

      {/* Newsletter opt-in */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-3 flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Newsletter</h3>
        </div>
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={newsletter}
            disabled={savingNews}
            onChange={(e) => toggleNewsletter(e.target.checked)}
            className="mt-1 h-5 w-5 shrink-0 rounded border-input accent-primary"
          />
          <span className="text-sm text-muted-foreground">
            Θέλω να λαμβάνω newsletter με προσφορές & νέα προϊόντα στο{" "}
            <span className="font-medium text-foreground">{user?.email}</span>. Μπορείς να
            απεγγραφείς οποιαδήποτε στιγμή.
          </span>
        </label>
      </div>

      {/* Data export */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-2 flex items-center gap-2">
          <Download className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Λήψη των δεδομένων μου</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Κατέβασε ένα αντίγραφο (JSON) με τα στοιχεία λογαριασμού, τις διευθύνσεις, το καλάθι
          και τις παραγγελίες σου.
        </p>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? "Προετοιμασία…" : "Λήψη δεδομένων μου"}
        </button>
      </div>

      {/* Account deletion */}
      <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
        <div className="mb-2 flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-destructive" />
          <h3 className="text-lg font-semibold text-destructive">Διαγραφή λογαριασμού</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Η ενέργεια είναι μη αναστρέψιμη. Διαγράφονται ο λογαριασμός σου, οι αποθηκευμένες
          διευθύνσεις και το καλάθι σου. Τα παραστατικά παραγγελιών διατηρούνται όσο απαιτεί η
          φορολογική νομοθεσία, αποσυνδεδεμένα από τον λογαριασμό σου.
        </p>

        {!showDelete ? (
          <button
            onClick={() => setShowDelete(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-destructive px-4 py-2.5 text-sm font-medium text-destructive transition hover:bg-destructive hover:text-destructive-foreground"
          >
            <Trash2 className="h-4 w-4" />
            Διαγραφή λογαριασμού
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              Πληκτρολόγησε <span className="text-destructive">ΔΙΑΓΡΑΦΗ</span> για επιβεβαίωση:
            </p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ΔΙΑΓΡΑΦΗ"
              className="w-full max-w-xs rounded-xl border border-input bg-background px-3 py-2 text-sm"
            />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleDelete}
                disabled={deleting || confirmText.trim().toUpperCase() !== "ΔΙΑΓΡΑΦΗ"}
                className="inline-flex items-center gap-2 rounded-xl bg-destructive px-4 py-2.5 text-sm font-medium text-destructive-foreground transition hover:bg-destructive/90 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleting ? "Διαγραφή…" : "Οριστική διαγραφή"}
              </button>
              <button
                onClick={() => {
                  setShowDelete(false);
                  setConfirmText("");
                }}
                disabled={deleting}
                className="rounded-xl border border-border px-4 py-2.5 text-sm hover:bg-muted"
              >
                Ακύρωση
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Για άλλα αιτήματα (διόρθωση, εναντίωση, φορητότητα) δες τη{" "}
        <Link to="/data-request" className="text-primary hover:underline">
          φόρμα αιτημάτων δεδομένων
        </Link>
        .
      </p>
    </div>
  );
}
